import { darkTheme, lightTheme } from '@tabbio/tokens';

export type ThemeMode = 'system' | 'light' | 'dark';

export const TABBIO_THEME_MODE_STORAGE_KEY = 'tabbio-theme-mode';

// Web-only: softer near-black instead of pure #000000 for the dark-mode
// page background. Native theming continues to use darkTheme.background.
const WEB_DARK_BG = '#0d0d0d';

export function isThemeMode(value: unknown): value is ThemeMode {
  return value === 'system' || value === 'light' || value === 'dark';
}

export function readStoredThemeMode(
  storage: Pick<Storage, 'getItem'> | null | undefined,
): ThemeMode {
  if (!storage) {
    return 'system';
  }

  const stored = storage.getItem(TABBIO_THEME_MODE_STORAGE_KEY);
  return isThemeMode(stored) ? stored : 'system';
}

export function getThemeBootstrapScript() {
  const lightName = 'light';
  const darkName = 'dark';

  // Pre-paint snapshot of all surface/text/border tokens: without this,
  // early-painting skeletons/modals fall back to transparent over white.
  const lightTokens = {
    background: lightTheme.background,
    surface: lightTheme.surface,
    surfaceSecondary: lightTheme.surfaceSecondary,
    surfaceTertiary: lightTheme.surfaceTertiary,
    surfaceElevated: lightTheme.surfaceElevated,
    text: lightTheme.text,
    textSecondary: lightTheme.textSecondary,
    textTertiary: lightTheme.textTertiary,
    border: lightTheme.border,
    borderSubtle: lightTheme.borderSubtle,
    borderStrong: lightTheme.borderStrong,
  };
  const darkTokens = {
    background: WEB_DARK_BG,
    surface: darkTheme.surface,
    surfaceSecondary: darkTheme.surfaceSecondary,
    surfaceTertiary: darkTheme.surfaceTertiary,
    surfaceElevated: darkTheme.surfaceElevated,
    text: darkTheme.text,
    textSecondary: darkTheme.textSecondary,
    textTertiary: darkTheme.textTertiary,
    border: darkTheme.border,
    borderSubtle: darkTheme.borderSubtle,
    borderStrong: darkTheme.borderStrong,
  };

  return `(function(){var storageKey=${JSON.stringify(TABBIO_THEME_MODE_STORAGE_KEY)};var mode="system";try{var stored=window.localStorage&&window.localStorage.getItem(storageKey);if(stored==="light"||stored==="dark"||stored==="system"){mode=stored;}}catch(e){}var prefersDark=!!(window.matchMedia&&window.matchMedia("(prefers-color-scheme: dark)").matches);var isDark=mode==="dark"||(mode!=="light"&&prefersDark);var name=isDark?${JSON.stringify(darkName)}:${JSON.stringify(lightName)};var t=isDark?${JSON.stringify(darkTokens)}:${JSON.stringify(lightTokens)};var root=document.documentElement;root.setAttribute("data-theme",name);root.style.backgroundColor=t.background;root.style.colorScheme=name;root.style.setProperty("--tabbio-bg",t.background);root.style.setProperty("--tabbio-text",t.text);root.style.setProperty("--tabbio-text-secondary",t.textSecondary);root.style.setProperty("--tabbio-text-tertiary",t.textTertiary);root.style.setProperty("--tabbio-surface",t.surface);root.style.setProperty("--tabbio-surface-secondary",t.surfaceSecondary);root.style.setProperty("--tabbio-surface-tertiary",t.surfaceTertiary);root.style.setProperty("--tabbio-surface-elevated",t.surfaceElevated);root.style.setProperty("--tabbio-border",t.border);root.style.setProperty("--tabbio-border-subtle",t.borderSubtle);root.style.setProperty("--tabbio-border-strong",t.borderStrong);var style=document.createElement("style");style.setAttribute("data-tabbio-theme-boot","true");style.textContent="html,body,#root{background:"+t.background+";color:"+t.text+";}";document.head.appendChild(style);try{var metas=document.querySelectorAll('meta[name="theme-color"]');for(var i=0;i<metas.length;i++){metas[i].parentNode.removeChild(metas[i]);}var live=document.createElement("meta");live.setAttribute("name","theme-color");live.setAttribute("content",t.background);live.setAttribute("data-tabbio-theme-color","true");document.head.appendChild(live);var statusStyle=isDark?"black-translucent":"default";var status=document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');if(!status){status=document.createElement("meta");status.setAttribute("name","apple-mobile-web-app-status-bar-style");document.head.appendChild(status);}status.setAttribute("content",statusStyle);}catch(e){}}())`;
}
